import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Info } from 'lucide-react';
import {
  useAuthSession,
  useStorePromotionalAds,
  useStoreWebsiteEditorStateV3,
  type StorePromotionalAdStoreContext,
  type SupabaseClient,
} from '@dukanti/shared';
import { useCurrentStore } from '@/hooks/use-current-store';
import { supabase } from '@/lib/supabase';
import { useWebsiteT } from '../hooks/use-website-t';
import { hasActiveAd, publishHidesLegacyAds } from '../lib/publish-ads';
import { isTemplateId } from '../lib/template-routes';

export interface PublishAdsNoticeProps {
  readonly client?: SupabaseClient;
}

/**
 * Epic #228 P7: a note in the publish question for a legacy store with active
 * ads: this design does not show them, and nothing is deleted. It never
 * blocks or changes the publish. Off the edit route (no template id) it
 * renders nothing and reads nothing.
 */
export function PublishAdsNotice({
  client = supabase,
}: PublishAdsNoticeProps): React.JSX.Element | null {
  const { templateId } = useParams();
  if (!isTemplateId(templateId)) return null;
  return <TemplateAdsNotice client={client} templateId={templateId} />;
}

interface TemplateAdsNoticeProps {
  readonly client: SupabaseClient;
  readonly templateId: string;
}

function TemplateAdsNotice({
  client,
  templateId,
}: TemplateAdsNoticeProps): React.JSX.Element | null {
  const { t } = useWebsiteT();
  const authSession = useAuthSession(client);
  const { storeId, storeSlug } = useCurrentStore(authSession);
  // The editor's own query key (the edit route reads the same template).
  const editor = useStoreWebsiteEditorStateV3(client, storeId, templateId);
  const adsAtRisk = publishHidesLegacyAds(editor.data);
  // Ads are read only when they matter: a legacy store publishing a design.
  const storeContext = useMemo<StorePromotionalAdStoreContext | null>(
    () => (adsAtRisk && storeId && storeSlug ? { storeId, storeSlug } : null),
    [adsAtRisk, storeId, storeSlug],
  );
  const ads = useStorePromotionalAds(client, storeContext);

  // Unknown is not a reason to warn: any error or missing data shows nothing.
  if (storeContext === null || ads.isError || !hasActiveAd(ads.ads)) return null;
  return (
    <p
      role="note"
      data-testid="publish-ads-notice"
      className="border-border bg-admin-surface-muted text-foreground flex items-start gap-2 rounded-lg border p-3 text-start text-sm"
    >
      <Info aria-hidden="true" className="text-muted-foreground mt-0.5 size-4 shrink-0" />
      <span>{t('admin.websiteTemplates.publish.adsNotice')}</span>
    </p>
  );
}
